import { CollectionViewPicker } from './collection-view.js';
import { inOptionOrder } from './choice-filters.js';
import {
  Button,
  Input,
  SegmentedControl,
  Card,
  Combobox,
  IconButton,
  Modal,
  MultiSelect,
  scrollMotion,
  useIsWideViewport,
} from '@hue/design-system';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  SlidersHorizontal,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Children, useEffect, useId, useRef, useState, type ReactNode } from 'react';

/// Peças comuns às telas de listagem: busca, filtro, ordenação e os estados de carregando, erro e
/// vazio. Ficam aqui para que uma tela nova não recrie — e faça divergir — o mesmo comportamento.

export function FilterGroup<T extends string>({
  label,
  description,
  options,
  value,
  onChange,
}: {
  label: string;
  description: string;
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <Combobox
      label={label}
      placeholder={description}
      selectedLabel={options.find((option) => option.value === value)?.label ?? ''}
      options={options}
      clearable={false}
      onSelect={(choice) => {
        if (choice.kind === 'option') {
          const option = options.find((item) => item.value === choice.value);
          if (option) onChange(option.value);
        }
      }}
    />
  );
}

/// Filtro que recorta registros: escolha múltipla (`docs/PADROES_DE_INTERFACE.md`). Nenhuma ou
/// todas marcadas é "todos"; a lista volta na ordem das opções, para a URL não mudar só porque a
/// pessoa marcou noutra ordem.
export function MultiFilter<T extends string>({
  label,
  itemNoun,
  allLabel,
  options,
  value,
  onChange,
  summary,
}: {
  label: string;
  /// Plural do que se escolhe, para o resumo: "2 de 3 tipos".
  itemNoun: string;
  /// O resumo quando nada ou tudo está marcado: "Todos os tipos".
  allLabel: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: readonly T[];
  onChange: (value: T[]) => void;
  /// Nome de uma combinação conhecida, no lugar da contagem.
  summary?: string | undefined;
}) {
  return (
    <MultiSelect
      label={label}
      itemNoun={itemNoun}
      allLabel={allLabel}
      noneLabel={allLabel}
      searchPlaceholder={`Buscar ${label.toLowerCase()}`}
      options={options}
      selected={value}
      onChange={(chosen) => onChange(inOptionOrder(chosen, options))}
      summary={summary}
    />
  );
}

/// Cabeçalho de coluna ordenável. A direção atual é anunciada por `aria-sort` na célula e repetida
/// em texto para leitor de tela, porque a seta sozinha não diz nada a quem não enxerga.
export function SortableColumn<T extends string>({
  column,
  label,
  sort,
  direction,
  onToggle,
}: {
  column: T;
  label: string;
  sort: T;
  direction: 'asc' | 'desc';
  onToggle: (column: T) => void;
}) {
  const active = sort === column;
  return (
    <th
      scope="col"
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-5 py-3 text-xs font-semibold text-content/70"
    >
      <button
        type="button"
        onClick={() => onToggle(column)}
        className="relative flex items-center gap-1.5 rounded-lg px-1 py-1 transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        {label}
        {active ? (
          direction === 'asc' ? (
            <ArrowUp className="size-3.5 text-brand-400" aria-hidden="true" />
          ) : (
            <ArrowDown className="size-3.5 text-brand-400" aria-hidden="true" />
          )
        ) : null}
        <span className="sr-only">
          {active && direction === 'asc'
            ? 'ordenado do menor para o maior'
            : active
              ? 'ordenado do maior para o menor'
              : 'ordenar por esta coluna'}
        </span>
      </button>
    </th>
  );
}

/// Resumo em faixa, não em cartões.
///
/// Três cartões grandes empurravam a lista para fora da tela: no celular era preciso rolar por
/// todos eles para chegar ao primeiro lançamento. Os mesmos números cabem em uma faixa de uma
/// linha, e o que a pessoa veio ver começa logo abaixo.
export function SummaryStrip({
  label,
  items,
  stackOnMobile = false,
}: {
  label: string;
  items: ReadonlyArray<{ label: string; value: string; tone?: 'positive' | 'warning' }>;
  stackOnMobile?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`mb-4 grid divide-surface-border overflow-hidden rounded-xl border border-surface-border bg-surface-card ${
        // Quatro números não cabem lado a lado no celular: viram duas fileiras de dois.
        stackOnMobile
          ? `grid-cols-1 divide-y sm:divide-y-0 sm:divide-x ${items.length === 4 ? 'sm:grid-cols-4' : items.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`
          : items.length === 4
            ? 'grid-cols-2 divide-x divide-y sm:grid-cols-4 sm:divide-y-0'
            : items.length === 2
              ? 'grid-cols-2 divide-x'
              : 'grid-cols-3 divide-x'
      }`}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className={`min-w-0 px-3 py-2.5 sm:px-4 ${stackOnMobile ? 'flex items-center justify-between gap-3 sm:block' : ''}`}
        >
          <p
            className={`${stackOnMobile ? 'sm:truncate' : 'truncate'} text-[10px] font-semibold uppercase tracking-[.1em] text-content-faint`}
          >
            {item.label}
          </p>
          <strong
            className={`mt-0.5 block ${stackOnMobile ? 'shrink-0 whitespace-nowrap sm:truncate' : 'truncate'} text-base font-bold tabular-nums tracking-[-.02em] sm:text-lg ${
              item.tone === 'positive'
                ? 'text-positive'
                : item.tone === 'warning'
                  ? 'text-warning'
                  : 'text-content'
            }`}
          >
            {item.value}
          </strong>
        </div>
      ))}
    </div>
  );
}

/// Barra de busca e filtros, grudada no topo — a mesma em todas as listagens.
///
/// Em tela larga os campos ficam à vista, ao lado da busca: há espaço, e esconder atrás de um
/// botão só acrescenta um clique a cada troca de filtro. No celular não há essa largura, então os
/// mesmos campos vão para um diálogo e o que está filtrando volta como etiqueta removível.
///
/// A alternativa seria cada tela decidir sozinha, e foi assim que lançamentos, aportes e sócios
/// acabaram com três filtros diferentes. Aqui a decisão é uma só: quem passa `children` ganha o
/// comportamento certo nos dois tamanhos, sem escrever nada sobre tamanho de tela.
/// Busca reutilizada por listas simples e barras com filtros avançados.
function ListSearchField({
  searchId,
  searchLabel,
  searchPlaceholder,
  search,
  onSearchChange,
}: {
  searchId: string;
  searchLabel: string;
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
}) {
  return (
    <Input
      id={searchId}
      type="text"
      data-slot="list-search"
      label={searchLabel}
      hideLabel
      placeholder={searchPlaceholder}
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      leftIcon={<Search className="size-4" />}
      rightIcon={
        search ? (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label={`Limpar ${searchLabel.toLowerCase()}`}
            className="grid size-10 place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : undefined
      }
    />
  );
}

/// A faixa dos filtros de uma página: presa no topo ao rolar, com a linha embaixo e a contagem à
/// direita. As duas barras usam esta mesma faixa, para a busca não mudar de lugar de uma tela para
/// outra.
const PAGE_FILTER_BAND =
  'sticky top-16 z-20 -mx-5 mb-4 border-b border-surface-border bg-surface/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:top-0';

/// Listas pequenas, sem paginação: busca, opções compactas e contagem. Na página fica na mesma
/// faixa da `FilterBar`; dentro de painel ou diálogo (`variant="panel"`), onde a faixa sairia pela
/// borda, fica solta.
export function ListFilterBar({
  searchLabel,
  searchPlaceholder = searchLabel,
  search,
  onSearchChange,
  count,
  variant = 'page',
  showViewPicker = true,
  children,
}: {
  searchLabel: string;
  searchPlaceholder?: string;
  search: string;
  onSearchChange: (value: string) => void;
  count: ReactNode;
  variant?: 'page' | 'panel';
  showViewPicker?: boolean;
  children?: ReactNode;
}) {
  const searchId = useId();
  const fields = (
    <>
      <div className="w-full min-w-[min(15rem,100%)] sm:max-w-sm sm:flex-1">
        <ListSearchField
          searchId={searchId}
          searchLabel={searchLabel}
          searchPlaceholder={searchPlaceholder}
          search={search}
          onSearchChange={onSearchChange}
        />
      </div>
      <div className="flex max-w-full flex-wrap items-end gap-3 empty:hidden">{children}</div>
    </>
  );
  const counter = (
    <p data-slot="list-count" className="text-xs text-content-faint" aria-live="polite">
      {count}
    </p>
  );
  if (variant === 'panel')
    return (
      <div data-slot="list-filter-bar" className="flex max-w-full flex-wrap items-end gap-3">
        {fields}
        {counter}
      </div>
    );
  return (
    <div
      data-slot="list-filter-bar"
      className={`${PAGE_FILTER_BAND} lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-x-3`}
    >
      <div className="flex max-w-full flex-wrap items-end gap-3">{fields}</div>
      <div className="mt-2 lg:mt-0 lg:flex lg:min-h-12 lg:items-center lg:gap-3">
        {counter}
        {showViewPicker ? <CollectionViewPicker /> : null}
      </div>
    </div>
  );
}

export function FilterBar({
  searchId,
  searchLabel,
  searchPlaceholder,
  search,
  onSearchChange,
  chips,
  onClear,
  count,
  resultsLabel,
  pageSize,
  onPageSizeChange,
  countActions,
  layout = 'inline',
  showViewPicker = true,
  children,
}: {
  searchId: string;
  searchLabel: string;
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  /// O que está filtrando agora, já em português. Tocar na etiqueta desfaz aquele filtro.
  chips: ReadonlyArray<{ key: string; label: string; onRemove: () => void }>;
  onClear: () => void;
  count: string;
  /// Texto do botão que fecha o diálogo no celular, já com a contagem ("Ver 12 lançamentos").
  resultsLabel: string;
  /// Quantos itens por página, quando a lista pagina. Fica aqui, ao lado da contagem, e não no
  /// rodapé: "63 lançamentos, mostrando 25 por vez" é uma frase só, e é onde a pessoa olha antes
  /// de rolar. No rodapé o campo ainda abria a lista de opções por cima da borda da tela no
  /// celular. Omitir os dois esconde o seletor — listas sem paginação não ganham um.
  pageSize?: number | undefined;
  onPageSizeChange?: ((size: number) => void) | undefined;
  /// Ações sobre todo o resultado filtrado, junto da contagem e do tamanho da página.
  countActions?: ReactNode;
  layout?: 'inline' | 'stacked';
  showViewPicker?: boolean;
  children: ReactNode;
}) {
  const wide = useIsWideViewport();
  const hasFilters = Children.toArray(children).length > 0;
  const [open, setOpen] = useState(false);

  return (
    <div
      data-slot="filter-bar"
      className={`${PAGE_FILTER_BAND} ${layout === 'inline' ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-x-3' : ''}`}
    >
      <div className="flex flex-wrap items-end gap-2 lg:gap-3">
        <div className="min-w-0 flex-1 lg:min-w-[15rem] lg:max-w-sm">
          <ListSearchField
            searchId={searchId}
            searchLabel={searchLabel}
            searchPlaceholder={searchPlaceholder}
            search={search}
            onSearchChange={onSearchChange}
          />
        </div>
        {wide || !hasFilters ? (
          children
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="hue-button flex h-12 shrink-0 items-center gap-2 rounded-xl border border-surface-border bg-surface-card px-3 text-sm font-semibold text-content-secondary transition hover:border-brand-500/50 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <SlidersHorizontal className="size-4" aria-hidden="true" />
            Filtros
            {chips.length > 0 ? (
              <span className="grid min-w-5 place-items-center rounded-full bg-brand-500 px-1 text-xs font-bold text-on-brand">
                {chips.length}
              </span>
            ) : null}
          </button>
        )}
      </div>

      {/* No celular a etiqueta é o único sinal do que está filtrando, já que os campos estão
          fechados. Em tela larga os próprios campos dizem isso, e repetir viraria ruído — sobra o
          "Limpar", que continua útil para desfazer tudo de uma vez. */}
      {chips.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 lg:col-span-2">
          {wide
            ? null
            : chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  aria-label={`Remover filtro ${chip.label}`}
                  className="flex items-center gap-1 rounded-full border border-brand-500/40 bg-brand-500/12 px-2.5 py-1 text-xs font-medium text-brand-400 transition hover:bg-brand-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  {chip.label}
                  <X className="size-3" aria-hidden="true" />
                </button>
              ))}
          <button
            type="button"
            onClick={onClear}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            Limpar filtros
          </button>
        </div>
      ) : null}

      <div
        className={`mt-2 flex flex-wrap items-center justify-between gap-3 ${layout === 'inline' ? 'lg:col-start-2 lg:row-start-1 lg:mt-0' : 'pt-2'}`}
      >
        <p data-slot="list-count" className="min-w-0 text-xs text-content-faint">
          {count}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {pageSize !== undefined && onPageSizeChange ? (
            <PageSizePicker size={pageSize} onChange={onPageSizeChange} />
          ) : null}
          {countActions}
          {showViewPicker ? <CollectionViewPicker /> : null}
        </div>
      </div>

      {wide ? null : (
        <Modal
          open={open}
          onOpenChange={setOpen}
          title="Filtros"
          description="A lista se atualiza enquanto você escolhe."
          footer={
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                disabled={chips.length === 0 && !search}
                onClick={onClear}
              >
                Limpar
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                {resultsLabel}
              </Button>
            </div>
          }
        >
          <div className="grid gap-5">{children}</div>
        </Modal>
      )}
    </div>
  );
}

const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/// Quantos itens por página, em controle segmentado.
///
/// São quatro opções de dois dígitos: cabem inteiras na linha, e cada troca vira um toque em vez
/// de abrir-escolher-fechar. No rodapé, onde isto morava antes, a lista suspensa ainda abria por
/// cima da borda inferior da tela no celular — problema que um chip não tem.
export function PageSizePicker({
  size,
  onChange,
  options = DEFAULT_PAGE_SIZE_OPTIONS,
}: {
  size: number;
  onChange: (size: number) => void;
  options?: readonly number[] | undefined;
}) {
  return (
    <div
      role="group"
      aria-label="Itens por página"
      className="flex shrink-0 items-center gap-1.5 text-xs text-content-faint"
    >
      <span aria-hidden="true">Por página</span>
      <SegmentedControl
        label="Quantidade por página"
        value={String(size)}
        onChange={(value) => onChange(Number(value))}
        className="[&_button]:min-h-8 [&_button]:basis-auto [&_button]:px-2 [&_button]:text-xs"
        options={options.map((option) => ({
          value: String(option),
          label: String(option),
          accessibleLabel: `${String(option)} por página`,
        }))}
      />
    </div>
  );
}

/// Ordenação onde não há cabeçalho de coluna para clicar.
///
/// Em tela estreita a tabela vira lista de cards, e com ela some o único lugar de onde se ordenava.
/// Estes chips repõem isso: tocar no que já está ativo inverte a direção, igual à coluna.
export function SortBar<T extends string>({
  columns,
  sort,
  direction,
  onToggle,
  label,
}: {
  columns: ReadonlyArray<{ sort: T; label: string }>;
  sort: T;
  direction: 'asc' | 'desc';
  onToggle: (column: T) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex flex-wrap items-center gap-1 border-b border-surface-border px-3 py-2"
    >
      <span className="px-1 text-xs text-content-faint">Ordenar</span>
      {columns.map((column) => {
        const active = sort === column.sort;
        return (
          <button
            key={column.sort}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(column.sort)}
            className={`inline-flex min-h-9 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
              active ? 'bg-brand-500 text-on-brand' : 'text-content-muted hover:text-content'
            }`}
          >
            {column.label}
            {active ? (
              direction === 'asc' ? (
                <ArrowUp className="size-3.5" aria-hidden="true" />
              ) : (
                <ArrowDown className="size-3.5" aria-hidden="true" />
              )
            ) : null}
            <span className="sr-only">
              {active && direction === 'asc'
                ? 'ordenado do menor para o maior'
                : active
                  ? 'ordenado do maior para o menor'
                  : 'ordenar por este campo'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function LoadingCard({ label }: { label: string }) {
  return (
    <Card className="grid min-h-52 place-items-center bg-surface-card">
      <div
        className="size-7 animate-spin rounded-full border-2 border-brand-500/20 border-t-brand-500"
        aria-label={label}
      />
    </Card>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="border-danger/30 bg-surface-card text-sm text-danger" role="alert">
      {message}
    </Card>
  );
}

/// A lista sem itens. O ícone entra como componente, e não pronto: com o ícone pronto cada tela
/// escolhia tamanho, cor e alinhamento, e o mesmo cartão saía de cinco jeitos.
export function EmptyCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card className="grid min-h-60 place-items-center bg-surface-card text-center">
      <div>
        <Icon className="mx-auto size-8 text-brand-400" aria-hidden="true" />
        <h2 className="mt-4 font-semibold">{title}</h2>
        <p className="mt-2 text-sm text-content-muted">{description}</p>
      </div>
    </Card>
  );
}

/// O rodapé de paginação do sistema: toda listagem paginada no servidor usa este, e não um
/// desenhado à mão para a tela. Duas telas resolvendo a mesma coisa por conta própria foi como a
/// auditoria ficou sem primeira/última página e sem escolha de quantidade — cada uma inventou o
/// próprio pedaço, e nenhuma inventou tudo.
///
/// Quantos itens por página não fica aqui: mora ao lado da contagem, no alto da `FilterBar`.
export function Pagination({
  total,
  limit,
  offset,
  onOffsetChange,
  itemLabel,
}: {
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (offset: number) => void;
  /// Nome do que está sendo contado, já flexionado ("registro"/"registros",
  /// "lançamento"/"lançamentos").
  itemLabel: (count: number) => string;
}) {
  // Lista vazia mostra o `EmptyCard` no lugar da tabela, e o rodapé some junto: "1–0 de 0" e
  // "Página 1 de 1" embaixo de um cartão vazio não dizem nada. Some aqui, e não em cada tela, para
  // nenhuma esquecer.
  if (total === 0) return null;
  const pageCount = Math.max(Math.ceil(total / limit), 1);
  const currentPage = Math.floor(offset / limit) + 1;
  const first = offset + 1;
  const last = Math.min(offset + limit, total);
  const atStart = offset === 0;
  const atEnd = last >= total;

  function goToPage(page: number) {
    onOffsetChange(Math.min(Math.max(page - 1, 0), pageCount - 1) * limit);
  }

  return (
    <div className="flex flex-col gap-3 border-t border-surface-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <p className="text-xs text-content-muted">
        {first}–{last} de {total} {itemLabel(total)}
      </p>
      <div
        role="group"
        aria-label="Navegar entre páginas"
        className="flex items-center justify-center gap-1"
      >
        <IconButton
          label="Primeira página"
          icon={<ChevronsLeft className="size-4" />}
          disabled={atStart}
          onClick={() => goToPage(1)}
        />
        <IconButton
          label="Página anterior"
          icon={<ChevronLeft className="size-4" />}
          disabled={atStart}
          onClick={() => goToPage(currentPage - 1)}
        />
        <span className="min-w-[6.5rem] text-center text-xs tabular-nums text-content-muted">
          Página {currentPage} de {pageCount}
        </span>
        <IconButton
          label="Próxima página"
          icon={<ChevronRight className="size-4" />}
          disabled={atEnd}
          onClick={() => goToPage(currentPage + 1)}
        />
        <IconButton
          label="Última página"
          icon={<ChevronsRight className="size-4" />}
          disabled={atEnd}
          onClick={() => goToPage(pageCount)}
        />
      </div>
    </div>
  );
}

/// A recusa costuma nascer no fim de um formulário longo, abaixo da dobra do modal; ela vem até a
/// vista para não parecer que o botão não fez nada.
export function RequestAlert({ message }: { message: string }) {
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    frame.current?.scrollIntoView?.({ block: 'nearest', behavior: scrollMotion() });
  }, [message]);
  return (
    <div
      ref={frame}
      role="alert"
      className="mt-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
    >
      {message}
    </div>
  );
}
